import React, { useState } from "react";
import { Colors } from "../../Constants/colors";
import { CircularProgress } from "@mui/material";
import { FontFamily } from "../../Constants/Enums/FontFamily.enums";
import { useMobile } from "../../hooks/deviceHooks/useDevice.hooks";
import {
  ALIGN_ITEMS,
  JUSTIFY_CONTENT,
  POSITION,
  SPACING,
} from "../../Constants/Enums/styles.enum";

export enum TextBtnType {
  primary = "primary",
  secondary = "secondary",
  danger = "danger",
  success = "success",
  info = "info",
}

export enum TextBtnSize {
  sm = "sm",
  md = "md",
  lg = "lg",
}

interface Props {
  onClick: () => unknown;
  type?: TextBtnType;
  title: string;
  size?: TextBtnSize;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  underline?: boolean;
  bold?: boolean;
  className?: string;
  additionalComponent?: React.ReactNode;
}

export const TextButton = ({
  onClick,
  type = TextBtnType.primary,
  title,
  size = TextBtnSize.md,
  disabled = false,
  loading = false,
  icon,
  underline = false,
  bold = false,
  className = "",
  additionalComponent,
}: Props) => {
  const [isHovered, setIsHovered] = useState(false);
  const isMobile = useMobile();

  const getButtonColor = () => {
    if (disabled) return Colors.AshRose;

    switch (type) {
      case TextBtnType.primary:
        return Colors.BurgundyRed;
      case TextBtnType.secondary:
        return Colors.TangerineOrange;
      case TextBtnType.danger:
        return Colors.Tomato;
      case TextBtnType.success:
        return "#10B981";
      case TextBtnType.info:
        return Colors.TiffanyBlue;
      default:
        return Colors.TangerineOrange;
    }
  };

  const getSizeStyles = () => {
    const baseSize = isMobile ? -2 : 0;
    switch (size) {
      case TextBtnSize.sm:
        return {
          fontSize: `${14 + baseSize}px`,
          iconSize: 16,
          padding: "6px 10px",
        };
      case TextBtnSize.md:
        return {
          fontSize: `${16 + baseSize}px`,
          iconSize: 18,
          padding: "8px 12px",
        };
      case TextBtnSize.lg:
        return {
          fontSize: `${18 + baseSize}px`,
          iconSize: 20,
          padding: "10px 14px",
        };
      default:
        return {
          fontSize: `${16 + baseSize}px`,
          iconSize: 18,
          padding: `${SPACING.S8} ${SPACING.S12}`,
        };
    }
  };

  const handleClick = () => {
    if (!disabled && !loading) {
      onClick();
    }
  };

  const sizeStyles = getSizeStyles();

  const buttonColor = getButtonColor();

  return (
    <button
      className={className}
      style={{
        display: "inline-flex",
        alignItems: ALIGN_ITEMS.CENTER,
        justifyContent: JUSTIFY_CONTENT.CENTER,
        color: buttonColor,
        fontSize: sizeStyles.fontSize,
        fontFamily: FontFamily.ROBOTO_REGULAR,
        fontWeight: bold ? 700 : 600,
        backgroundColor: "transparent",
        border: "none",
        cursor: disabled || loading ? "not-allowed" : "pointer",
        padding: sizeStyles.padding,
        borderRadius: "4px",
        opacity: disabled ? 0.5 : 1,
        textDecoration: "none",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        transform:
          isHovered && !disabled && !loading ? "translateY(-1px)" : "none",
        outline: "none",
        position: POSITION.RELATIVE,
        whiteSpace: "nowrap",
        boxShadow: "none",
        letterSpacing: "0.5px",
      }}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled || loading}
      type="button"
    >
      {loading ? (
        <CircularProgress
          variant="indeterminate"
          size={sizeStyles.iconSize}
          style={{ color: buttonColor }}
        />
      ) : (
        <span
          style={{
            display: "inline-flex",
            alignItems: ALIGN_ITEMS.CENTER,
            position: POSITION.RELATIVE,
          }}
        >
          {icon && (
            <span
              style={{
                display: "inline-flex",
                alignItems: ALIGN_ITEMS.CENTER,
                justifyContent: JUSTIFY_CONTENT.CENTER,
                lineHeight: 0,
                transform: isHovered ? "scale(1.1)" : "scale(1)",
                transition: "transform 0.2s ease",
                marginRight: "4px",
              }}
            >
              {icon}
            </span>
          )}

          {additionalComponent && (
            <span
              style={{
                display: "inline-flex",
                alignItems: ALIGN_ITEMS.CENTER,
                justifyContent: JUSTIFY_CONTENT.CENTER,
                lineHeight: 0,
                marginRight: "4px",
              }}
            >
              {additionalComponent}
            </span>
          )}

          <span
            style={{
              lineHeight: 1.2,
            }}
          >
            {title}
          </span>

          <span
            style={{
              position: POSITION.ABSOLUTE,
              bottom: "-3px",
              left: "0",
              right: "0",
              height: isHovered ? "3px" : "2px",
              backgroundColor: buttonColor,
              borderRadius: "2px",
              transform: isHovered ? "scaleX(1)" : "scaleX(0.95)",
              transition: "all 0.2s ease",
              opacity: underline || !isHovered ? 1 : 0.85,
            }}
          />
        </span>
      )}
    </button>
  );
};
